/**
 * MONSTER TOWN — THE JOB LOOP. OWNED BY THE VENUE CREW (src/venue/**).
 *
 * This is the reason building has a purpose. A monster sits down and asks for
 * something in pictures; the kid makes it in steps at the stations they built
 * (base -> scoops -> topping), carries it over, and hands it across. Right
 * order: everybody is delighted, coins fly, a friend turns up. Wrong order:
 * a giggle, keep what you made, try again. That is the entire failure model.
 *
 * NOTHING IN HERE IS VENUE-SPECIFIC. Every station, seat and menu item comes
 * from the `VenueDef` data, so a zoo (base = bowl, scoop = food, topping =
 * treat) runs on this exact code.
 *
 * HOW A TAP GETS HERE: the router turns a tap into `play:interactRequested`,
 * the monster crew walks over and emits `play:interacted`, and the frozen
 * interaction runner fires the placeable's own reaction. We subscribe to the
 * same event and add the SHOP MEANING of that tap. We never call `interact()`.
 */

import * as THREE from 'three'
import { bus } from '../core/events'
import { engine } from '../core/engine'
import { wallet } from '../core/economy'
import { getPlaceable } from '../core/placeable'
import { registerSaveSection, save } from '../core/save'
import { world } from '../core/scene'
import type { InstanceId, MonsterConfig, PlaceableId, SceneId, VenueId } from '../core/types'
import type { VenueOrder } from './types'
import { app, type GameContext } from '../app/context'
import { worldToCanvasPx } from '../input/pick'
import { css } from '../core/palette'
import { cylinder, group } from '../render/kit'
import { trayArt } from './art'
import { VenueCrowd } from './customers'
import { availableItems } from './orders'
import type { VenueAnchor } from './events'
import {
  EMPTY_PICS,
  allVenues,
  emptyTray,
  picturesFor,
  trayIsEmpty,
  venueForScene,
  type VenueDef,
  type VenueTray,
} from './types'

/** Generous headroom over `maxScoops` so a happy tapper is never blocked. */
const TRAY_SCOOP_CAP = 5

interface VenueCounters {
  served: number
  tips: number
}

const crowds = new Map<SceneId, VenueCrowd>()
const counters = new Map<VenueId, VenueCounters>()
let tray: VenueTray = emptyTray()
let trayGroup: THREE.Group | null = null
let traySceneId: SceneId | null = null
let trayBob = 0
let lifetimeServed = 0

export function currentTray(): VenueTray {
  return { base: tray.base, scoops: [...tray.scoops], topping: tray.topping }
}

export function venueCounters(id: VenueId): VenueCounters {
  let c = counters.get(id)
  if (c === undefined) {
    c = { served: 0, tips: 0 }
    counters.set(id, c)
  }
  return c
}

export function totalServed(): number {
  return lifetimeServed
}

export function crowdFor(sceneId: SceneId): VenueCrowd | undefined {
  return crowds.get(sceneId)
}

/**
 * Why the shop was last emptied. Customers live in the `actors` layer, which is
 * never saved, so they legitimately go home on a load or a scene change — but a
 * mystery clear is a bug, and this is how you catch it from the harness.
 */
const clearLog: { count: number; last: string } = { count: 0, last: 'never' }

export function crowdClearLog(): { count: number; last: string } {
  return { ...clearLog }
}

function clearAllCrowds(reason: string): void {
  clearLog.count++
  clearLog.last = reason
  for (const c of crowds.values()) c.clear()
}

// ---------------------------------------------------------------------------
// tray
// ---------------------------------------------------------------------------

/**
 * Say what is on the tray. The tray belongs to whichever venue the kid is
 * standing in; outside one there is no menu to resolve pictures against, so it
 * reports empty and the HUD chip goes away with the room.
 */
function emitTray(): void {
  const def = world.activeSceneId === null ? undefined : venueForScene(world.activeSceneId)
  const pics = def === undefined ? EMPTY_PICS : picturesFor(def.items, tray)
  bus.emit('venue:trayChanged', { tray: currentTray(), pics })
}

function trayChanged(): void {
  emitTray()
  rebuildTrayArt()
  save.request('venue:tray')
}

function rebuildTrayArt(): void {
  if (trayGroup !== null) {
    trayGroup.removeFromParent()
    trayGroup = null
  }
  const sceneId = world.activeSceneId
  if (sceneId === null) return
  const def = venueForScene(sceneId)
  if (def === undefined || trayIsEmpty(tray)) return
  const node = world.require(sceneId)
  const g = group('heldTray')
  // A little plate under it so it reads as CARRIED, not as a hat or a prop
  // sitting on the floor. The plate is also the thing a kid's eye tracks.
  g.add(cylinder(0.3, 0.05, { color: 'plate', seg: 14, name: 'trayPlate' }))
  const food = trayArt(tray, def.items, 1)
  food.position.y = 0.05
  g.add(food)
  node.actors.add(g)
  trayGroup = g
  traySceneId = sceneId
}

function addToTray(def: VenueDef, itemId: string): void {
  const item = def.items.find((i) => i.id === itemId)
  if (item === undefined) return
  if (item.kind === 'base') tray.base = item.id
  else if (item.kind === 'topping') tray.topping = item.id
  else if (tray.scoops.length < TRAY_SCOOP_CAP) tray.scoops.push(item.id)
  trayChanged()
}

function clearTray(): void {
  tray = emptyTray()
  trayChanged()
}

/** One tap builds the whole sundae — what the expensive pedestal buys you. */
function makeSpecial(def: VenueDef, node: ReturnType<typeof world.require>): void {
  const avail = availableItems(node, def)
  const base = avail.find((i) => i.kind === 'base')
  const scoops = avail.filter((i) => i.kind === 'scoop')
  const top = avail.find((i) => i.kind === 'topping')
  tray = emptyTray()
  if (base !== undefined) tray.base = base.id
  for (let i = 0; i < Math.min(2, Math.max(1, scoops.length)); i++) {
    const s = scoops[i % Math.max(1, scoops.length)]
    if (s !== undefined) tray.scoops.push(s.id)
  }
  if (top !== undefined) tray.topping = top.id
  trayChanged()
}

/**
 * ONE place posts an order, so the pictures can never be resolved differently
 * by the live loop and by the staging path (which is what a screenshot sees).
 */
function postOrder(def: VenueDef, posted: { key: string; order: VenueOrder; look: MonsterConfig }): void {
  bus.emit('venue:ordered', {
    venueId: def.id,
    key: posted.key,
    order: posted.order,
    look: posted.look,
    pics: picturesFor(def.items, posted.order),
  })
}

// ---------------------------------------------------------------------------
// serving
// ---------------------------------------------------------------------------

function serve(def: VenueDef, seatId: InstanceId): void {
  const crowd = crowds.get(def.sceneId)
  if (crowd === undefined) return
  const out = crowd.serveAt(seatId, tray)
  const at = { ...app.playerPos }

  if (out.result === 'correct') {
    const c = venueCounters(def.id)
    c.served += 1
    lifetimeServed += 1
    // Coins come EASILY (brief): pay per scoop, always tip, and add a bonus
    // for the fancy orders. A couple of customers buys a new flavour.
    const paid = wallet.earn('venue.served', at, Math.max(1, out.scoops))
    const tip = wallet.earn('venue.tip', at)
    const fancy = out.order !== null && (out.order.topping !== null || out.scoops >= 2)
    const bonus = fancy ? wallet.earn('venue.perfectOrder', at) : 0
    c.tips += tip + bonus
    clearTray()
    crowd.hurryNext()
    bus.emit('venue:served', {
      venueId: def.id,
      key: out.key ?? null,
      correct: true,
      coins: paid + tip + bonus,
    })
    bus.emit('venue:friendComing', { venueId: def.id })
    bus.emit('audio:cue', { cue: 'serve_happy' })
    bus.emit('ui:toast', { icon: 'heart' })
    save.request('venue:served')
    return
  }

  if (out.result === 'wrong' || out.result === 'empty') {
    // A giggle and the order goes back up. The tray is NOT taken away — the
    // kid keeps what they made and can add to it. Never a fail (LAW #2).
    bus.emit('venue:served', { venueId: def.id, key: out.key ?? null, correct: false, coins: 0 })
    bus.emit('audio:cue', { cue: 'giggle' })
    bus.emit('ui:toast', { icon: 'giggle' })
  }
}

// ---------------------------------------------------------------------------
// tap routing — DATA, not a switch on hard-coded ids
// ---------------------------------------------------------------------------

type VenueAction =
  | { kind: 'add'; itemId: string }
  | { kind: 'serve' }
  | { kind: 'clear' }
  | { kind: 'bell' }
  | { kind: 'hint' }
  | { kind: 'special' }

/**
 * A FIXTURE'S ROLE IS A TAG, NOT ITS ID.
 *
 * This used to compare against the literal ids `venue_bin`, `venue_counter`,
 * `venue_menu` and `venue_special` — four hard-coded ice-cream-shop nouns in the
 * one file that is supposed to know nothing about any particular venue. It also
 * meant venue #2 could not have its OWN bin or its own bell without being a
 * recolour of the ice cream shop's, which is precisely how the restaurant ended
 * up sharing the parlour's counter and teal pedal bin.
 *
 * A restaurant declares a wooden scraps pail tagged `venue:clear` and it just
 * works. So does a zoo's feed bucket.
 */
const ROLE_TAGS: readonly { readonly tag: string; readonly action: VenueAction }[] = [
  { tag: 'venue:clear', action: { kind: 'clear' } },
  { tag: 'venue:bell', action: { kind: 'bell' } },
  { tag: 'venue:hint', action: { kind: 'hint' } },
  { tag: 'venue:special', action: { kind: 'special' } },
]

function actionFor(def: VenueDef, defId: PlaceableId): VenueAction | null {
  const item = def.items.find((i) => i.stationDefId === defId)
  if (item !== undefined) return { kind: 'add', itemId: item.id }
  if (def.seatDefIds.includes(defId)) return { kind: 'serve' }
  const tags = getPlaceable(defId)?.tags ?? []
  for (const r of ROLE_TAGS) if (tags.includes(r.tag)) return r.action
  return null
}

function onInteracted(e: { instanceId: InstanceId; defId: PlaceableId; sceneId: SceneId }): void {
  const def = venueForScene(e.sceneId)
  if (def === undefined) return
  const action = actionFor(def, e.defId)
  if (action === null) return
  const node = world.require(e.sceneId)
  const crowd = crowds.get(e.sceneId)

  switch (action.kind) {
    case 'add':
      addToTray(def, action.itemId)
      break
    case 'serve':
      serve(def, e.instanceId)
      break
    case 'clear':
      clearTray()
      break
    case 'bell':
      if (crowd !== undefined && !crowd.spawnNow()) crowd.hint()
      break
    case 'hint':
      crowd?.hint()
      break
    case 'special':
      makeSpecial(def, node)
      break
  }
}

// ---------------------------------------------------------------------------
// WHERE THE CARDS GO, AND WHEN THEY GO AWAY
// ---------------------------------------------------------------------------

/**
 * Publish every open order's on-screen position.
 *
 * Cards are anchored to their monster, so the HUD needs this every frame. It is
 * at most four projections plus four object allocations on the busiest frame
 * this room ever has, and it early-outs to a single emit of an empty list when
 * nobody is waiting — which is also the signal that clears the rail.
 *
 * The projection goes through `worldToCanvasPx`, which measures the CANVAS with
 * `getBoundingClientRect()`. Never window metrics: on an older iPad the window
 * height does not match the canvas and every card would sit one slot off.
 */
let anchorsWereEmpty = true

function publishAnchors(ctx: GameContext, crowd: VenueCrowd | undefined): void {
  const list = crowd?.anchors() ?? []
  if (list.length === 0) {
    // Emit the empty list ONCE, then stay quiet until there is something to say.
    if (anchorsWereEmpty) return
    anchorsWereEmpty = true
    bus.emit('venue:anchors', { anchors: [] })
    return
  }
  anchorsWereEmpty = false
  const out: VenueAnchor[] = []
  for (const a of list) {
    const p = worldToCanvasPx(ctx.camera.camera, ctx.canvas, { x: a.x, y: a.y, z: a.z })
    out.push({ key: a.key, x: p.x, y: p.y, color: css(a.color) })
  }
  bus.emit('venue:anchors', { anchors: out })
}

/**
 * THE VENUE MUST CLOSE ON EVERY PATH OUT OF IT, NOT JUST THE POLITE ONE.
 *
 * `scene:left` only fires from `world.transitionTo`. The harness — and anything
 * else that jumps scenes with `world.setActiveImmediate` — fires no scene events
 * at all, and the captured frame of the town shop showed the result: three ice
 * cream order tickets pinned across the top and the serving-tray pill along the
 * bottom while the kid stood in a furniture shop. The crowd was still simulating
 * in a room nobody was in, too.
 *
 * Watching the active scene id catches all of them, because there is no way to
 * change scenes without changing it.
 */
let watchedScene: SceneId | null = null
let openVenue: VenueId | null = null

function watchActiveScene(sceneId: SceneId | null): void {
  if (sceneId === watchedScene) return
  watchedScene = sceneId
  const def = sceneId === null ? undefined : venueForScene(sceneId)
  if (def === undefined) {
    if (openVenue !== null) closeVenue()
    return
  }
  if (openVenue !== def.id) {
    if (openVenue !== null) closeVenue()
    openVenue = def.id
  }
}

function closeVenue(): void {
  const was = openVenue
  openVenue = null
  clearAllCrowds('venue:closed')
  if (trayGroup !== null) {
    trayGroup.removeFromParent()
    trayGroup = null
  }
  if (!anchorsWereEmpty) {
    anchorsWereEmpty = true
    bus.emit('venue:anchors', { anchors: [] })
  }
  bus.emit('venue:closed', { venueId: was })
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

interface VenueSave {
  lifetimeServed: number
  counters: Record<string, VenueCounters>
  tray: { base: string | null; scoops: string[]; topping: string | null }
}

export function installVenueService(ctx: GameContext): void {
  registerSaveSection<VenueSave>(
    {
      id: 'venue',
      version: 1,
      defaults: () => ({ lifetimeServed: 0, counters: {}, tray: { base: null, scoops: [], topping: null } }),
      serialize: () => ({
        lifetimeServed,
        counters: Object.fromEntries([...counters].map(([k, v]) => [k, { ...v }])),
        tray: { base: tray.base, scoops: [...tray.scoops], topping: tray.topping },
      }),
      hydrate: (raw, c) => {
        if (!c.isRecord(raw)) return c.defaults
        const outCounters: Record<string, VenueCounters> = {}
        const rc = c.isRecord(raw['counters']) ? raw['counters'] : {}
        for (const [k, v] of Object.entries(rc)) {
          if (!c.isRecord(v)) continue
          outCounters[k] = {
            served: Math.max(0, c.num(v['served'], 0)),
            tips: Math.max(0, c.num(v['tips'], 0)),
          }
        }
        const rt = c.isRecord(raw['tray']) ? raw['tray'] : {}
        return {
          lifetimeServed: Math.max(0, c.num(raw['lifetimeServed'], 0)),
          counters: outCounters,
          tray: {
            base: typeof rt['base'] === 'string' ? rt['base'] : null,
            scoops: c
              .arr<string>(rt['scoops'], (e) => (typeof e === 'string' ? e : null))
              .slice(0, TRAY_SCOOP_CAP),
            topping: typeof rt['topping'] === 'string' ? rt['topping'] : null,
          },
        }
      },
    },
    (v) => {
      lifetimeServed = v.lifetimeServed
      counters.clear()
      for (const [k, c] of Object.entries(v.counters)) counters.set(k as VenueId, { ...c })
      tray = { base: v.tray.base, scoops: [...v.tray.scoops], topping: v.tray.topping }
      // Everyone in the shop goes home on a load; they walk back in on entry.
      clearAllCrowds('save:loaded')
      rebuildTrayArt()
    },
  )

  for (const def of allVenues()) {
    crowds.set(def.sceneId, new VenueCrowd(def, engine.rng))
  }

  bus.on('play:interacted', onInteracted)

  bus.on('scene:entered', (e) => {
    const def = venueForScene(e.scene)
    rebuildTrayArt()
    // Re-state the tray against THIS room's menu, so a tray carried in from a
    // save (or from another venue) is drawn with the right pictures — or with
    // none at all outside a venue.
    emitTray()
    if (def === undefined) return
    const node = world.require(e.scene)
    const crowd = crowds.get(e.scene)
    if (crowd === undefined) return
    crowd.attach(node)
    clearLog.count++
    clearLog.last = `scene:entered:${e.scene}`
    crowd.clear()
    bus.emit('venue:opened', { venueId: def.id })
    bus.emit('audio:cue', { cue: 'shop_open' })
  })

  bus.on('scene:left', (e) => {
    if (crowds.has(e.scene)) {
      clearLog.count++
      clearLog.last = `scene:left:${e.scene}`
    }
    crowds.get(e.scene)?.clear()
    if (traySceneId === e.scene && trayGroup !== null) {
      trayGroup.removeFromParent()
      trayGroup = null
    }
  })

  engine.addSystem({
    id: 'venue.service',
    order: 52,
    fixed: (dt) => {
      // Cheapest possible early-out: `fixed` runs up to four times a frame and
      // the kid is in the town almost all of the time.
      const sceneId = world.activeSceneId
      if (sceneId === null) return
      const crowd = crowds.get(sceneId)
      if (crowd === undefined) return
      const def = venueForScene(sceneId)
      if (def === undefined) return
      crowd.update(dt, lifetimeServed, (posted) => {
        postOrder(def, posted)
        bus.emit('audio:cue', { cue: 'order_up' })
      })
      trayBob += dt
    },
    render: () => {
      const sceneId = world.activeSceneId
      watchActiveScene(sceneId)
      const crowd = sceneId === null ? undefined : crowds.get(sceneId)
      if (crowd === undefined && trayGroup === null) return
      const cp = ctx.camera.camera.position
      const tp = ctx.camera.target
      const yaw = Math.atan2(cp.x - tp.x, cp.z - tp.z)
      crowd?.renderFrame(yaw)
      publishAnchors(ctx, crowd)
      if (trayGroup !== null) {
        // Held OUT IN FRONT, at chest height. Any higher and it reads as an ice
        // cream hat rather than something the monster is carrying.
        trayGroup.position.set(
          app.playerPos.x + Math.sin(yaw) * 1.0,
          0.35 + Math.sin(trayBob * 2.2) * 0.03,
          app.playerPos.z + Math.cos(yaw) * 1.0,
        )
        trayGroup.rotation.y = yaw
      }
    },
  })
}

/** Harness/debug: set up a believable service moment for a screenshot. */
export function debugStageService(sceneId: SceneId): number {
  const def = venueForScene(sceneId)
  const crowd = crowds.get(sceneId)
  if (def === undefined || crowd === undefined) return 0
  crowd.attach(world.require(sceneId))
  let n = 0
  while (crowd.spawnNow()) n++
  // Fast-forward the walk-in and the ordering beat, POSTING THE ORDERS as the
  // live loop does. This used to swallow them with a no-op callback, so a staged
  // shop had three monsters waiting and an empty ticket rail — the orders only
  // ever appeared in captures because the UI crew's `tickets()` demo painted
  // fake ones. Now that the rail is the one place an order is drawn, staging
  // without posting would mean staging a shop with no orders in it at all.
  for (let i = 0; i < 140; i++) {
    crowd.update(1 / 30, lifetimeServed, (posted) => postOrder(def, posted))
  }
  const w = crowd.anyWaiting()
  if (w !== null) {
    tray = { base: w.order.base, scoops: [...w.order.scoops], topping: w.order.topping }
    trayChanged()
  }
  return n
}

/** Harness/debug: serve whoever is waiting, exactly the way a kid would. */
export function debugServeOne(sceneId: SceneId, correct: boolean): 'correct' | 'wrong' | 'none' {
  const def = venueForScene(sceneId)
  const crowd = crowds.get(sceneId)
  if (def === undefined || crowd === undefined) return 'none'
  const w = crowd.anyWaiting()
  if (w === null) return 'none'
  tray = correct
    ? { base: w.order.base, scoops: [...w.order.scoops], topping: w.order.topping }
    : { base: w.order.base, scoops: [...w.order.scoops, ...w.order.scoops], topping: null }
  serve(def, w.seatId)
  return correct ? 'correct' : 'wrong'
}
